// src/Pages/Explore.js
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import {
  db,
  ymd,
  addDays,
  endExclusiveFromEndDate,
  checkAvailabilityManyDay,
  checkAvailabilityManyRange,
} from "../firebase";

/* ------------------------------ Routes --------------------------------- */
const ROUTES = {
  home: "/",
  hostNew: "/host/new",
  listing: (id) => `/listing/${encodeURIComponent(id)}`,
};

/* ------------------------------ Config --------------------------------- */
const PAGE_SIZE = 12;
const AVAIL_BATCH_CAP = 24;
const SAGE_COLOR = "#6C7F72";

/* ------------------------------ Labels --------------------------------- */
const BOOKING_MODE_LABEL = {
  day: "Daytime",
  overnight: "Overnight",
  hybrid: "Day + Overnight",
};

const SPACE_TYPE_LABEL = {
  entire_place: "Entire place",
  private_room: "Private room",
  shared_room: "Shared room",
  outdoor_space: "Outdoor",
  other: "Other",
};

const SPACE_TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "entire_place", label: SPACE_TYPE_LABEL.entire_place },
  { value: "private_room", label: SPACE_TYPE_LABEL.private_room },
  { value: "shared_room", label: SPACE_TYPE_LABEL.shared_room },
  { value: "outdoor_space", label: SPACE_TYPE_LABEL.outdoor_space },
  { value: "other", label: SPACE_TYPE_LABEL.other },
];

/* ------------------------------ Utils ---------------------------------- */
const formatMoney = (n) =>
  n == null || isNaN(n) ? "—" : `$${Number(n).toLocaleString()}`;

const todayYYYYMMDD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function toGuestPrice(base, precomputed, feePct = 15) {
  const p = Number(precomputed);
  if (Number.isFinite(p) && p > 0) return Math.round(p);
  const b = Number(base);
  if (!Number.isFinite(b) || b <= 0) return null;
  return Math.round(b * (1 + feePct / 100));
}

function normalizeListing(id, data) {
  const dayPrice = typeof data.dayPrice === "number" ? data.dayPrice : 
                   typeof data.dayRate === "number" ? data.dayRate : null;
  const nightlyPrice = typeof data.nightlyPrice === "number" ? data.nightlyPrice : null;

  const bookingMode = data.bookingMode === "overnight" || 
                     data.bookingMode === "hybrid" || 
                     data.bookingMode === "day" ? data.bookingMode :
                     nightlyPrice ? "overnight" : "day";

  const guestDayPrice = toGuestPrice(dayPrice, data.guestDayPrice);
  const guestNightlyPrice = toGuestPrice(nightlyPrice, data.guestNightlyPrice);
  const stateCode = data.stateCode || data.state || "";
  const spaceType = data.spaceType || "entire_place";
  const spaceName = (data.spaceName || "").trim() || null;
  const entranceType = data.entranceType || "shared";
  const areas = Array.isArray(data.areas) ? data.areas : [];

  return {
    id,
    ownerUid: data.ownerUid || "",
    title: data.title || "",
    summary: data.summary || "",
    capacity: Number(data.capacity ?? 0),
    address: data.address || "",
    city: data.city || "",
    stateCode,
    bookingMode,
    dayPrice: dayPrice != null ? Number(dayPrice) : null,
    nightlyPrice: nightlyPrice != null ? Number(nightlyPrice) : null,
    guestDayPrice,
    guestNightlyPrice,
    minNights: typeof data.minNights === "number" ? Number(data.minNights) : null,
    maxNights: typeof data.maxNights === "number" ? Number(data.maxNights) : null,
    photos: Array.isArray(data.photos) ? data.photos : 
            Array.isArray(data.images) ? data.images : [],
    spaceType,
    spaceName,
    entranceType,
    areas,
    // New fields from CreateListing.jsx
    propertyType: data.propertyType || "",
    bedrooms: Number(data.bedrooms ?? 0),
    beds: Number(data.beds ?? 0),
    bathrooms: parseFloat(data.bathrooms ?? 0),
    status: data.status || "draft",
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
    publishedAt: data.publishedAt ?? null,
  };
}

const supportsDay = (l) =>
  (l.bookingMode === "day" || l.bookingMode === "hybrid") &&
  (Number(l.dayPrice) > 0 || Number(l.guestDayPrice) > 0);

const supportsStay = (l) =>
  (l.bookingMode === "overnight" || l.bookingMode === "hybrid") &&
  (Number(l.nightlyPrice) > 0 || Number(l.guestNightlyPrice) > 0);

/* -------------------------------- Main Component -------------------------------- */
export default function Explore() {
  const [params, setParams] = useSearchParams();

  const [docs, setDocs] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [intent, setIntent] = useState(params.get("mode") === "overnight" ? "overnight" : "day");
  const [search, setSearch] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [spaceTypeFilter, setSpaceTypeFilter] = useState(params.get("sp") || "all");
  const [minPrice, setMinPrice] = useState(params.get("min") || "");
  const [maxPrice, setMaxPrice] = useState(params.get("max") || "");
  const [minCap, setMinCap] = useState(params.get("cap") || "");
  const [nights, setNights] = useState(params.get("n") || "");
  const [sort, setSort] = useState(params.get("sort") || "newest");

  const [dayDate, setDayDate] = useState(params.get("date") || "");
  const [stayStart, setStayStart] = useState(params.get("start") || "");
  const [stayEnd, setStayEnd] = useState(params.get("end") || "");

  const [availabilityMap, setAvailabilityMap] = useState({});
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const availabilityCacheRef = useRef({});
  const minDate = todayYYYYMMDD();
  const searchRef = useRef(null);
  const sentinelRef = useRef(null);

  const deferredSearch = useDeferredValue(search);
  const deferredCity = useDeferredValue(city);
  const deferredSpaceType = useDeferredValue(spaceTypeFilter);
  const deferredMinPrice = useDeferredValue(minPrice);
  const deferredMaxPrice = useDeferredValue(maxPrice);
  const deferredMinCap = useDeferredValue(minCap);
  const deferredNights = useDeferredValue(nights);

  useEffect(() => {
    document.title = "Explore spaces • You're Welcome";
  }, []);

  useEffect(() => {
    const next = new URLSearchParams();
    if (intent !== "day") next.set("mode", "overnight");
    if (deferredSearch) next.set("q", deferredSearch);
    if (deferredCity) next.set("city", deferredCity);
    if (deferredSpaceType && deferredSpaceType !== "all") next.set("sp", deferredSpaceType);
    if (deferredMinPrice) next.set("min", deferredMinPrice);
    if (deferredMaxPrice) next.set("max", deferredMaxPrice);
    if (deferredMinCap) next.set("cap", deferredMinCap);
    if (deferredNights) next.set("n", deferredNights);
    if (sort !== "newest") next.set("sort", sort);
    if (intent === "day" && dayDate) next.set("date", dayDate);
    if (intent === "overnight") {
      if (stayStart) next.set("start", stayStart);
      if (stayEnd) next.set("end", stayEnd);
    }
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [
    intent, deferredSearch, deferredCity, deferredSpaceType,
    deferredMinPrice, deferredMaxPrice, deferredMinCap, deferredNights,
    sort, dayDate, stayStart, stayEnd, params, setParams
  ]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const qRef = query(
          collection(db, "listings"),
          where("status", "==", "published"),
          orderBy("publishedAt", "desc"),
          limit(PAGE_SIZE)
        );
        const snap = await getDocs(qRef);
        if (!mounted) return;
        const items = snap.docs.map((d) => normalizeListing(d.id, d.data()));
        setDocs(items);
        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.docs.length === PAGE_SIZE);
      } catch (e) {
        console.error(e);
        setError("Couldn't load spaces. Please try again.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const loadMore = useCallback(async () => {
    if (!hasMore || !lastDoc || loadingMore) return;
    setLoadingMore(true);
    setError("");
    try {
      const qRef = query(
        collection(db, "listings"),
        where("status", "==", "published"),
        orderBy("publishedAt", "desc"),
        startAfter(lastDoc),
        limit(PAGE_SIZE)
      );
      const snap = await getDocs(qRef);
      const items = snap.docs.map((d) => normalizeListing(d.id, d.data()));
      setDocs((prev) => [...prev, ...items]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setError("Couldn't load more spaces.");
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, lastDoc, loadingMore]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries || [];
        if (entry?.isIntersecting) loadMore();
      },
      { rootMargin: "600px 0px 0px 0px", threshold: 0.01 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const filtered = useMemo(() => {
    const s = (deferredSearch || "").trim().toLowerCase();
    const wantDay = intent === "day";
    const wantStay = intent === "overnight";
    const nightsNum = deferredNights ? Number(deferredNights) : null;

    let out = docs.filter((d) => {
      if (wantDay && !supportsDay(d)) return false;
      if (wantStay && !supportsStay(d)) return false;

      if (deferredSpaceType !== "all" && d.spaceType !== deferredSpaceType) return false;

      const inSearch = !s || [d.title, d.summary, d.city, d.stateCode, d.spaceName]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(s));

      const inCity = !deferredCity || (d.city || "").toLowerCase() === deferredCity.toLowerCase();

      const guestPrice = wantDay ? Number(d.guestDayPrice ?? NaN) : Number(d.guestNightlyPrice ?? NaN);
      const inMinPrice = !deferredMinPrice || (!isNaN(guestPrice) && guestPrice >= Number(deferredMinPrice));
      const inMaxPrice = !deferredMaxPrice || (!isNaN(guestPrice) && guestPrice <= Number(deferredMaxPrice));

      const cap = Number(d.capacity);
      const inCap = !deferredMinCap || (!isNaN(cap) && cap >= Number(deferredMinCap));

      const nightsOK = !wantStay || !nightsNum || 
        ((d.minNights == null || nightsNum >= d.minNights) &&
         (d.maxNights == null || nightsNum <= d.maxNights));

      return inSearch && inCity && inMinPrice && inMaxPrice && inCap && nightsOK;
    });

    if (sort === "price_asc") {
      out.sort((a, b) => {
        const av = intent === "day" ? a.guestDayPrice ?? 0 : a.guestNightlyPrice ?? 0;
        const bv = intent === "day" ? b.guestDayPrice ?? 0 : b.guestNightlyPrice ?? 0;
        return av - bv;
      });
    } else if (sort === "price_desc") {
      out.sort((a, b) => {
        const av = intent === "day" ? a.guestDayPrice ?? 0 : a.guestNightlyPrice ?? 0;
        const bv = intent === "day" ? b.guestDayPrice ?? 0 : b.guestNightlyPrice ?? 0;
        return bv - av;
      });
    } else {
      out.sort((a, b) => {
        const aT = a?.publishedAt?.toMillis?.() ?? a?.createdAt?.toMillis?.() ?? 0;
        const bT = b?.publishedAt?.toMillis?.() ?? b?.createdAt?.toMillis?.() ?? 0;
        if (bT !== aT) return bT - aT;
        return String(a.title || "").localeCompare(String(b.title || ""));
      });
    }

    return out;
  }, [
    docs, deferredSearch, deferredCity, deferredSpaceType,
    deferredMinPrice, deferredMaxPrice, deferredMinCap,
    deferredNights, sort, intent
  ]);

  const hasActiveFilters = Boolean(
    deferredSearch || deferredCity || deferredMinPrice || deferredMaxPrice ||
    deferredMinCap || deferredNights || dayDate || stayStart || stayEnd ||
    (deferredSpaceType && deferredSpaceType !== "all")
  ) || sort !== "newest" || intent !== "day";

  const clearFilters = useCallback(() => {
    setSearch("");
    setCity("");
    setSpaceTypeFilter("all");
    setMinPrice("");
    setMaxPrice("");
    setMinCap("");
    setNights("");
    setDayDate("");
    setStayStart("");
    setStayEnd("");
    setSort("newest");
    setIntent("day");
    setAvailabilityMap({});
    availabilityCacheRef.current = {};
    setParams(new URLSearchParams(), { replace: true });
    searchRef.current?.focus();
  }, [setParams]);

  useEffect(() => {
    let canceled = false;
    const timer = setTimeout(async () => {
      const needsDay = intent === "day" && !!dayDate;
      const needsSpan = intent === "overnight" && !!stayStart && !!stayEnd;

      if ((!needsDay && !needsSpan) || filtered.length === 0) {
        setAvailabilityMap({});
        return;
      }

      const ids = filtered.slice(0, AVAIL_BATCH_CAP).map((l) => l.id);
      const queryKey = needsDay ? `day:${dayDate}` : `stay:${stayStart}|${stayEnd}`;
      const cache = availabilityCacheRef.current;
      const toCheck = [];
      const interimMap = {};

      ids.forEach((id) => {
        const k = `${queryKey}:${id}`;
        if (k in cache) {
          interimMap[id] = cache[k];
        } else {
          toCheck.push(id);
        }
      });

      if (toCheck.length === 0) {
        if (!canceled) setAvailabilityMap(interimMap);
        return;
      }

      setCheckingAvail(true);
      try {
        let results = {};
        if (needsDay) {
          results = await checkAvailabilityManyDay(toCheck, dayDate);
        } else if (needsSpan) {
          const endExclusive = endExclusiveFromEndDate(stayEnd) || ymd(addDays(stayEnd, 1));
          results = await checkAvailabilityManyRange(toCheck, stayStart, endExclusive);
        }
        if (canceled) return;

        for (const id of toCheck) {
          const k = `${queryKey}:${id}`;
          const v = typeof results[id] === "boolean" ? results[id] : null;
          cache[k] = v;
          interimMap[id] = v;
        }
        availabilityCacheRef.current = cache;
        setAvailabilityMap(interimMap);
      } catch {
        if (!canceled) {
          for (const id of toCheck) {
            const k = `${queryKey}:${id}`;
            cache[k] = null;
            interimMap[id] = null;
          }
          availabilityCacheRef.current = cache;
          setAvailabilityMap(interimMap);
        }
      } finally {
        if (!canceled) setCheckingAvail(false);
      }
    }, 250);

    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [filtered, intent, dayDate, stayStart, stayEnd]);

  const activeChips = [
    intent !== "day" ? { k: "intent", label: "Stay mode", onClear: () => setIntent("day") } : null,
    spaceTypeFilter !== "all" ? {
      k: "spaceType",
      label: SPACE_TYPE_LABEL[spaceTypeFilter],
      onClear: () => setSpaceTypeFilter("all"),
    } : null,
    dayDate && intent === "day" ? { k: "dayDate", label: dayDate, onClear: () => setDayDate("") } : null,
    stayStart && intent === "overnight" ? { k: "stayStart", label: `Start: ${stayStart}`, onClear: () => setStayStart("") } : null,
    stayEnd && intent === "overnight" ? { k: "stayEnd", label: `End: ${stayEnd}`, onClear: () => setStayEnd("") } : null,
    search ? { k: "search", label: `"${search}"`, onClear: () => setSearch("") } : null,
    city ? { k: "city", label: city, onClear: () => setCity("") } : null,
    minPrice ? { k: "minPrice", label: `≥$${minPrice}`, onClear: () => setMinPrice("") } : null,
    maxPrice ? { k: "maxPrice", label: `≤$${maxPrice}`, onClear: () => setMaxPrice("") } : null,
    minCap ? { k: "minCap", label: `${minCap}+ guests`, onClear: () => setMinCap("") } : null,
    nights ? {
      k: "nights",
      label: `${nights} night${Number(nights) === 1 ? "" : "s"}`,
      onClear: () => setNights(""),
    } : null,
    sort !== "newest" ? {
      k: "sort",
      label: sort === "price_asc" ? "Low → High" : sort === "price_desc" ? "High → Low" : "Newest",
      onClear: () => setSort("newest"),
    } : null,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      {/* Mobile-First Header */}
      <header className="sticky top-0 z-50 border-b border-neutral-200 bg-white">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to={ROUTES.home} className="block" aria-label="Home">
              <img
                src="/YoureWelcome Logo.png"
                alt="You're Welcome"
                className="h-9 w-auto"
                width={108}
                height={36}
                decoding="async"
              />
            </Link>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              <Link
                to={ROUTES.hostNew}
                className="hidden rounded-full bg-black px-4 py-2 text-sm font-semibold text-white sm:inline-block"
              >
                Become a host
              </Link>
              <button className="rounded-full border border-neutral-300 p-2">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div className="px-4 pb-3 sm:hidden">
          <div className="relative">
            <button
              onClick={() => setFiltersOpen(true)}
              className="flex w-full items-center justify-between rounded-full border border-neutral-300 bg-white px-4 py-3 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full" style={{ backgroundColor: SAGE_COLOR }}>
                  <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium">Anywhere</div>
                  <div className="text-xs text-neutral-500">Add guests</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Desktop Search Bar - Clean & Minimal */}
      <div className="hidden border-b border-neutral-200 sm:block">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="rounded-full border border-neutral-300 bg-white shadow-sm">
            <div className="flex items-center divide-x divide-neutral-300">
              {/* Where */}
              <div className="flex-1 px-6 py-4">
                <div className="mb-1 text-xs font-medium text-neutral-700">Where</div>
                <input
                  ref={searchRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search destinations"
                  className="w-full border-none p-0 text-sm outline-none placeholder:text-neutral-500"
                />
              </div>

              {/* Who */}
              <div className="flex flex-1 items-center justify-between px-6 py-4">
                <div>
                  <div className="mb-1 text-xs font-medium text-neutral-700">Who</div>
                  <input
                    type="number"
                    min="1"
                    value={minCap}
                    onChange={(e) => setMinCap(e.target.value)}
                    className="w-full border-none p-0 text-sm outline-none"
                    placeholder="Add guests"
                  />
                </div>
              </div>

              {/* Search Button */}
              <button
                onClick={() => {}}
                className="flex h-12 w-12 items-center justify-center rounded-full m-2"
                style={{ backgroundColor: SAGE_COLOR }}
                aria-label="Search"
              >
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Small Mode Toggle */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-full border border-neutral-300 p-1">
                <button
                  onClick={() => setIntent("day")}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    intent === "day" 
                      ? "bg-black text-white" 
                      : "text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  Daytime
                </button>
                <button
                  onClick={() => setIntent("overnight")}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    intent === "overnight" 
                      ? "bg-black text-white" 
                      : "text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  Overnight
                </button>
              </div>

              <button
                onClick={() => setFiltersOpen(true)}
                className="flex items-center gap-2 rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs hover:bg-neutral-50"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="border-b border-neutral-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-3">
            <div className="flex items-center gap-2 overflow-x-auto">
              {activeChips.map((chip) => (
                <button
                  key={chip.k}
                  onClick={chip.onClear}
                  className="flex shrink-0 items-center gap-1 rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs hover:bg-neutral-50"
                >
                  {chip.label}
                  <span className="ml-1 text-neutral-400">×</span>
                </button>
              ))}
              <button
                onClick={clearFilters}
                className="shrink-0 text-sm text-neutral-600 hover:text-neutral-900"
              >
                Clear all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Header */}
      <div className="border-b border-neutral-200">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="text-sm font-medium text-neutral-900">
            {loading ? "Loading spaces..." : `${filtered.length} space${filtered.length === 1 ? "" : "s"}`}
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm outline-none"
          >
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      {/* Main Content - Mobile Optimized */}
      <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <SkeletonGrid />
        ) : filtered.length === 0 ? (
          <EmptyState onReset={clearFilters} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((l) => (
                <ListingCard
                  key={l.id}
                  listing={l}
                  intent={intent}
                  dayDate={dayDate}
                  stayStart={stayStart}
                  stayEnd={stayEnd}
                  availabilityMap={availabilityMap}
                  checkingAvail={checkingAvail}
                />
              ))}
            </div>

            {hasMore && (
              <>
                <div ref={sentinelRef} className="h-10 w-full" />
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="rounded-full border border-neutral-300 px-6 py-2 text-sm hover:bg-neutral-50 disabled:opacity-50"
                  >
                    {loadingMore ? "Loading..." : "Load more"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-neutral-200 bg-white py-2 sm:hidden">
        <div className="grid grid-cols-4 text-center">
          <Link to="/" className="flex flex-col items-center gap-1 text-black">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="text-xs font-medium">Explore</span>
          </Link>
          
          <Link to="/wishlists" className="flex flex-col items-center gap-1 text-neutral-500">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span className="text-xs">Wishlists</span>
          </Link>
          
          <Link to="/trips" className="flex flex-col items-center gap-1 text-neutral-500">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-xs">Trips</span>
          </Link>
          
          <Link to="/profile" className="flex flex-col items-center gap-1 text-neutral-500">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-xs">Profile</span>
          </Link>
        </div>
      </nav>

      {/* Filters Modal - Mobile First */}
      {filtersOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white sm:bg-black/50">
          <div className="flex flex-1 flex-col sm:flex-row">
            {/* Mobile header */}
            <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-4 sm:hidden">
              <button
                onClick={() => setFiltersOpen(false)}
                className="text-neutral-600"
              >
                Cancel
              </button>
              <h2 className="text-base font-semibold">Filters</h2>
              <button
                onClick={() => setFiltersOpen(false)}
                className="font-medium text-black"
              >
                Done
              </button>
            </div>

            {/* Desktop overlay */}
            <div className="hidden flex-1 sm:block" onClick={() => setFiltersOpen(false)} />

            {/* Filters content */}
            <div className="flex-1 overflow-y-auto bg-white p-4 sm:max-w-md sm:rounded-l-2xl">
              <div className="hidden items-center justify-between pb-6 sm:flex">
                <h2 className="text-xl font-semibold">Filters</h2>
                <button
                  onClick={() => setFiltersOpen(false)}
                  className="rounded-full p-2 hover:bg-neutral-100"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Type */}
                <div>
                  <h3 className="mb-3 text-sm font-medium">Type of stay</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setIntent("day")}
                      className={`rounded-lg border p-3 text-center ${
                        intent === "day"
                          ? "border-black bg-black text-white"
                          : "border-neutral-300 hover:border-neutral-400"
                      }`}
                    >
                      Daytime
                    </button>
                    <button
                      onClick={() => setIntent("overnight")}
                      className={`rounded-lg border p-3 text-center ${
                        intent === "overnight"
                          ? "border-black bg-black text-white"
                          : "border-neutral-300 hover:border-neutral-400"
                      }`}
                    >
                      Overnight
                    </button>
                  </div>
                </div>

                {/* Location - Only City input, no presets */}
                <div>
                  <h3 className="mb-3 text-sm font-medium">Location</h3>
                  <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City, state, or region"
                    className="w-full rounded-lg border border-neutral-300 p-3 text-sm"
                  />
                </div>

                {/* Price Range */}
                <div>
                  <h3 className="mb-3 text-sm font-medium">Price range</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs text-neutral-600">Min</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">$</span>
                        <input
                          type="number"
                          value={minPrice}
                          onChange={(e) => setMinPrice(e.target.value)}
                          placeholder="0"
                          className="w-full rounded-lg border border-neutral-300 py-3 pl-8 pr-3 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-neutral-600">Max</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">$</span>
                        <input
                          type="number"
                          value={maxPrice}
                          onChange={(e) => setMaxPrice(e.target.value)}
                          placeholder="Any"
                          className="w-full rounded-lg border border-neutral-300 py-3 pl-8 pr-3 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Other Filters */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="mb-3 text-sm font-medium">Space type</h3>
                    <select
                      value={spaceTypeFilter}
                      onChange={(e) => setSpaceTypeFilter(e.target.value)}
                      className="w-full rounded-lg border border-neutral-300 p-3 text-sm"
                    >
                      {SPACE_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <h3 className="mb-3 text-sm font-medium">Guests</h3>
                    <input
                      type="number"
                      min="1"
                      value={minCap}
                      onChange={(e) => setMinCap(e.target.value)}
                      placeholder="Any"
                      className="w-full rounded-lg border border-neutral-300 p-3 text-sm"
                    />
                  </div>
                </div>

                {intent === "overnight" && (
                  <div>
                    <h3 className="mb-3 text-sm font-medium">Nights</h3>
                    <input
                      type="number"
                      min="1"
                      value={nights}
                      onChange={(e) => setNights(e.target.value)}
                      placeholder="Any"
                      className="w-full rounded-lg border border-neutral-300 p-3 text-sm"
                    />
                  </div>
                )}

                {/* Sort */}
                <div>
                  <h3 className="mb-3 text-sm font-medium">Sort by</h3>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 p-3 text-sm"
                  >
                    <option value="newest">Newest first</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={clearFilters}
                    className="flex-1 rounded-lg border border-neutral-300 py-3 text-sm hover:bg-neutral-50"
                  >
                    Clear all
                  </button>
                  <button
                    onClick={() => setFiltersOpen(false)}
                    className="flex-1 rounded-lg bg-black py-3 text-sm font-medium text-white hover:bg-neutral-800"
                  >
                    Show results
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------- Components ------------------------------- */

function ListingCard({
  listing,
  intent,
  dayDate,
  stayStart,
  stayEnd,
  availabilityMap,
  checkingAvail,
}) {
  const {
    id,
    title,
    summary,
    city,
    stateCode,
    beds,
    photos,
    spaceType,
    spaceName,
    guestDayPrice,
    guestNightlyPrice,
  } = listing;

  const cover = Array.isArray(photos) && photos.length > 0 ? photos[0] : null;
  const location = [city, stateCode].filter(Boolean).join(", ");
  const showDay = intent === "day";
  const priceValue = showDay ? guestDayPrice : guestNightlyPrice;
  const unit = showDay ? "/day" : "/night";

  const params = new URLSearchParams();
  params.set("mode", intent);
  if (intent === "day" && dayDate) params.set("date", dayDate);
  if (intent === "overnight" && stayStart) params.set("start", stayStart);
  if (intent === "overnight" && stayEnd) params.set("end", stayEnd);

  const hasDayQuery = intent === "day" && !!dayDate;
  const hasSpanQuery = intent === "overnight" && !!stayStart && !!stayEnd;
  const avail = availabilityMap?.[id];

  let availLabel = null;
  if (hasDayQuery || hasSpanQuery) {
    if (avail === true) {
      availLabel = "Available";
    } else if (avail === false) {
      availLabel = "Unavailable";
    } else if (checkingAvail) {
      availLabel = "Checking...";
    }
  }

  return (
    <Link
      to={{
        pathname: ROUTES.listing(id),
        search: params.toString() ? `?${params.toString()}` : "",
      }}
      className="group"
    >
      <div className="overflow-hidden rounded-xl">
        <div className="relative aspect-[3/2] overflow-hidden rounded-xl bg-neutral-100">
          {cover ? (
            <img
              src={cover}
              alt={title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-neutral-400">
              No image
            </div>
          )}
          <button
            className="absolute right-3 top-3 rounded-full bg-white/90 p-2 shadow-sm hover:bg-white"
            onClick={(e) => {
              e.preventDefault();
              // Save functionality
            }}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>

        <div className="mt-3">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-semibold">{spaceName || title}</h3>
              <p className="mt-1 truncate text-xs text-neutral-600">{location}</p>
            </div>
            <div className="ml-2 flex items-center gap-1">
              <span className="text-sm font-semibold">{formatMoney(priceValue)}</span>
              <span className="text-xs text-neutral-600">{unit}</span>
            </div>
          </div>

          {summary && (
            <p className="mt-2 line-clamp-2 text-xs text-neutral-600">{summary}</p>
          )}

          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-neutral-600">
              {/* Show beds instead of guests */}
              {beds > 0 && (
                <span>{beds} bed{beds !== 1 ? 's' : ''}</span>
              )}
              {spaceType !== "entire_place" && (
                <span className="capitalize">{spaceType.replace('_', ' ')}</span>
              )}
            </div>
            
            {availLabel && (
              <span className={`rounded-full px-2 py-1 text-xs ${
                avail === true ? 'bg-green-100 text-green-700' :
                avail === false ? 'bg-red-100 text-red-700' :
                'bg-neutral-100 text-neutral-600'
              }`}>
                {availLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="aspect-[3/2] rounded-xl bg-neutral-200" />
          <div className="mt-3 space-y-2">
            <div className="h-4 w-3/4 rounded bg-neutral-200" />
            <div className="h-3 w-1/2 rounded bg-neutral-200" />
            <div className="h-3 w-1/4 rounded bg-neutral-200" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onReset }) {
  return (
    <div className="py-16 text-center">
      <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-neutral-100 flex items-center justify-center">
        <svg className="h-8 w-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      </div>
      <h3 className="mb-2 text-lg font-semibold">No spaces match your search</h3>
      <p className="mb-6 text-neutral-600">Try adjusting your filters or search terms</p>
      <button
        onClick={onReset}
        className="rounded-full bg-black px-6 py-2 text-sm font-medium text-white hover:bg-neutral-800"
      >
        Clear all filters
      </button>
    </div>
  );
}